import React, { useState, useEffect, useCallback } from 'react';
import AdminTableSkeleton from "../../components/layout/AdminTableSkeleton";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import api from '../../api/api';
import { Plus, Edit, Trash2, Save, X, RotateCcw } from 'lucide-react';
import AdminOnboardingChecklist from './AdminOnboardingChecklist';
import AdminResources from './AdminResources';

export default function LookupManagement() {
    const { user, can } = useAuth();
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();
    const [lookups, setLookups] = useState({
        personas: [],
        intents: [],
        industries: [],
        positions: [],
    });
    const [activeTab, setActiveTab] = useState('personas');

    // Open the first tab this role can view
    useEffect(() => {
        const allowed = ['personas', 'intents', 'industries', 'positions', 'onboarding', 'resources'].filter((tab) => can('Lookups', tab));
        if (allowed.length && !allowed.includes(activeTab)) setActiveTab(allowed[0]);
    }, [can, activeTab]);
    const [statusTab, setStatusTab] = useState('active');
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [newItem, setNewItem] = useState({ name: '', description: '' });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!user || !user.isAdmin) {
            navigate('/userdashboard');
        }
    }, [user, navigate]);

    const fetchLookups = useCallback(async () => {
        try {
            setLoading(true);
            const [personasRes, intentsRes, industriesRes, positionsRes] = await Promise.all([
                api.get('/admin/lookups/personas').catch(() => ({ data: { items: [] } })),
                api.get('/admin/lookups/intents').catch(() => ({ data: { items: [] } })),
                api.get('/admin/lookups/industries').catch(() => ({ data: { items: [] } })),
                api.get('/admin/lookups/positions').catch(() => ({ data: { items: [] } })),
            ]);
            setLookups({
                personas: personasRes.data.items || [],
                intents: intentsRes.data.items || [],
                industries: industriesRes.data.items || [],
                positions: positionsRes.data.items || []
            });
        } catch (err) {
            console.error('Error fetching lookups:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchLookups(); }, [fetchLookups]);

    const handleCreate = async () => {
        if (!newItem.name.trim()) return;
        setSaving(true);
        try {
            await api.post(`/admin/lookups/${activeTab}`, { name: newItem.name, description: newItem.description });
            setNewItem({ name: '', description: '' });
            await fetchLookups();
        } catch (err) {
            console.error('Error creating lookup:', err);
            alert(err.response?.data?.error || 'Failed to create lookup item');
        } finally {
            setSaving(false);
        }
    };

    const handleUpdate = async (item) => {
        setSaving(true);
        try {
            await api.put(`/admin/lookups/${activeTab}/${item.id}`, { name: item.name, description: item.description || '', is_active: item.is_active !== false });
            setEditing(null);
            await fetchLookups();
        } catch (err) {
            console.error('Error updating lookup:', err);
            alert(err.response?.data?.error || 'Failed to update lookup item');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (item) => {
        if (!window.confirm(`Are you sure you want to delete "${item.name}"? It will be moved to the Inactive tab and can be restored later.`)) return;
        setSaving(true);
        try {
            await api.delete(`/admin/lookups/${activeTab}/${item.id}`);
            await fetchLookups();
        } catch (err) {
            console.error('Error deleting lookup:', err);
            alert(err.response?.data?.error || 'Failed to delete lookup item');
        } finally {
            setSaving(false);
        }
    };

    const handleRestore = async (item) => {
        setSaving(true);
        try {
            await api.patch(`/admin/lookups/${activeTab}/${item.id}/restore`);
            await fetchLookups();
        } catch (err) {
            console.error('Error restoring lookup:', err);
            alert(err.response?.data?.error || 'Failed to restore lookup item');
        } finally {
            setSaving(false);
        }
    };

    const handleHardDelete = async (item) => {
        if (!window.confirm(`Permanently delete "${item.name}"? This cannot be undone.`)) return;
        setSaving(true);
        try {
            await api.delete(`/admin/lookups/${activeTab}/${item.id}/permanent`);
            await fetchLookups();
        } catch (err) {
            console.error('Error permanently deleting lookup:', err);
            alert(err.response?.data?.error || 'Failed to permanently delete lookup item');
        } finally {
            setSaving(false);
        }
    };

    const isOnboardingTab = activeTab === 'onboarding';
    const isResourcesTab = activeTab === 'resources';
    const allLookups = lookups[activeTab] || [];
    const activeLookups = allLookups.filter((item) => item.is_active !== false);
    const inactiveLookups = allLookups.filter((item) => item.is_active === false);
    const currentLookups = isOnboardingTab ? [] : (statusTab === 'active' ? activeLookups : inactiveLookups);

    if (loading) {
        return <AdminTableSkeleton isDarkMode={isDarkMode} maxW="max-w-7xl" withSubtitle tabs={6} rows={8} cols={4} />;
    }

    const inputCls = `px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100 placeholder-slate-400' : 'border-slate-300'}`;

    return (
        <div className={`min-h-screen ${isDarkMode ? 'bg-surface' : 'bg-surface'}`}>
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Header */}
                <header className="mb-6">
                    <button onClick={() => navigate('/admin/dashboard')} className="text-primary-600 hover:text-primary-700 mb-4 text-sm font-medium">
                        &larr; Back to Dashboard
                    </button>
                    <h1 className={`text-3xl font-extrabold tracking-tight mb-2 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Lookup Management</h1>
                    <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>Manage personas, intents, industries, positions, and onboarding tasks</p>
                </header>

                {/* Tabs */}
                <div className={`rounded-2xl border shadow-sm mb-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                    <div className={`flex gap-1 px-4 pt-4 pb-0 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                        {['personas', 'intents', 'industries', 'positions', 'onboarding', 'resources'].filter((tab) => can('Lookups', tab)).map((tab) => {
                            const count = (tab === 'onboarding' || tab === 'resources') ? null : (lookups[tab] || []).filter((item) => item.is_active !== false).length;
                            return (
                                <button
                                    key={tab}
                                    onClick={() => {
                                        setActiveTab(tab);
                                        setStatusTab('active');
                                        setEditing(null);
                                        setNewItem({ name: '', description: '' });
                                    }}
                                    className={`px-4 py-2.5 text-sm font-semibold capitalize rounded-t-lg transition-all whitespace-nowrap ${
                                        activeTab === tab
                                            ? 'text-primary-700 border-b-2 border-primary-600 bg-primary-50'
                                            : isDarkMode
                                                ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-700 border-b-2 border-transparent'
                                                : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50 border-b-2 border-transparent'
                                    }`}
                                >
                                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                                    {count !== null && (
                                        <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${
                                            activeTab === tab
                                                ? 'bg-primary-100 text-primary-700'
                                                : isDarkMode
                                                    ? 'bg-slate-700 text-slate-400'
                                                    : 'bg-slate-100 text-slate-500'
                                        }`}>
                                            {count}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {isResourcesTab ? (
                        <div className="p-0">
                            <AdminResources embedded />
                        </div>
                    ) : isOnboardingTab ? (
                        <div className="p-0">
                            <AdminOnboardingChecklist embedded />
                        </div>
                    ) : (
                        <>
                            {/* Active / Inactive sub-tabs */}
                            <div className={`flex gap-2 px-5 pt-4 ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
                                {[
                                    { key: 'active', label: 'Active', items: activeLookups },
                                    { key: 'inactive', label: 'Inactive', items: inactiveLookups },
                                ].map((st) => (
                                    <button
                                        key={st.key}
                                        onClick={() => { setStatusTab(st.key); setEditing(null); }}
                                        className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-all ${
                                            statusTab === st.key
                                                ? (st.key === 'inactive' ? 'bg-slate-700 text-white' : 'bg-primary-600 text-white')
                                                : isDarkMode
                                                    ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                        }`}
                                    >
                                        {st.label} ({st.items.length})
                                    </button>
                                ))}
                            </div>

                            {/* Add New Item */}
                            <div className={`p-5 border-b ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-gradient-to-r from-slate-50 to-white'}`}>
                                <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Add New {activeTab.charAt(0).toUpperCase() + activeTab.slice(1, -1)}</p>
                                <div className="flex gap-3">
                                    <input
                                        type="text"
                                        placeholder="Name"
                                        value={newItem.name}
                                        onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                                        className={`flex-1 px-4 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200 placeholder-slate-500' : 'border-slate-300'}`}
                                    />
                                    {(activeTab === 'personas' || activeTab === 'intents') && (
                                        <input
                                            type="text"
                                            placeholder="Description (optional)"
                                            value={newItem.description}
                                            onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                                            className={`flex-1 px-4 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200 placeholder-slate-500' : 'border-slate-300'}`}
                                        />
                                    )}
                                    <button
                                        onClick={handleCreate}
                                        disabled={saving}
                                        className="px-5 py-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 active:scale-[0.97] transition-all flex items-center gap-2 text-sm font-semibold shadow-sm"
                                    >
                                        <Plus size={16} />
                                        Add
                                    </button>
                                </div>
                            </div>

                            {/* Lookup Items List */}
                            <div className="p-5">
                                {currentLookups.length === 0 ? (
                                    <p className={`text-center py-12 text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                        {statusTab === 'inactive' ? `No inactive ${activeTab}.` : `No ${activeTab} found. Add one above to get started.`}
                                    </p>
                                ) : (
                                    <div className="space-y-2">
                                        {currentLookups.map((item) => (
                                            <div
                                                key={item.id}
                                                className={`flex items-center gap-4 p-4 border rounded-xl transition-all ${isDarkMode ? 'border-slate-700 hover:bg-slate-700 hover:shadow-sm' : 'border-slate-200 hover:bg-slate-50 hover:shadow-sm'}`}
                                            >
                                                {editing?.id === item.id ? (
                                                    <>
                                                        <input
                                                            type="text"
                                                            value={editing.name}
                                                            onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                                                            className={`flex-1 px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200' : 'border-slate-300'}`}
                                                        />
                                                        {(activeTab === 'personas' || activeTab === 'intents') && (
                                                            <input
                                                                type="text"
                                                                value={editing.description || ''}
                                                                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                                                                placeholder="Description"
                                                                className={`flex-1 px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200' : 'border-slate-300'}`}
                                                            />
                                                        )}
                                                        <button
                                                            onClick={() => handleUpdate(editing)}
                                                            className="p-2 text-primary-600 hover:bg-primary-100 rounded-lg transition-colors" title="Save"
                                                        >
                                                            <Save size={18} />
                                                        </button>
                                                        <button
                                                            onClick={() => setEditing(null)}
                                                            className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg transition-colors" title="Cancel"
                                                        >
                                                            <X size={18} />
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className="flex-1">
                                                          <div className={`font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{item.name}</div>
                                                          {(activeTab === 'personas' || activeTab === 'intents') && item.description && (
                                                              <div className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{item.description}</div>
                                                          )}
                                                        </div>
                                                        <button
                                                          onClick={() => setEditing({ ...item })}
                                                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit"
                                                        >
                                                          <Edit size={16} />
                                                        </button>
                                                        {statusTab === 'active' ? (
                                                            <button
                                                              onClick={() => handleDelete(item)}
                                                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Delete"
                                                            >
                                                              <Trash2 size={16} />
                                                            </button>
                                                        ) : (
                                                            <>
                                                                <button
                                                                  onClick={() => handleRestore(item)}
                                                                  className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors" title="Restore"
                                                                >
                                                                  <RotateCcw size={16} />
                                                                </button>
                                                                <button
                                                                  onClick={() => handleHardDelete(item)}
                                                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete Permanently"
                                                                >
                                                                  <Trash2 size={16} />
                                                                </button>
                                                            </>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
